import classNames from 'classnames';
import * as React from 'react';
import { ColorResult } from 'react-color';

import { MaskedTextInput } from '../../../components/MaskedTextInput/MaskedTextInput';
import { PopupDecorator } from '../../../components/PopupDecorator/PopupDecorator';
import { Bem } from '../../../service/Bem/Bem';
import { ThemePopup } from '../../../typings/common';
import { ColorPicker } from '../../ColorPicker/ColorPicker';
import { LimitedColorPicker } from '../../LimitedColorPicker/LimitedColorPicker';

import './ColorEditor.css';

export interface ColorEditorProps {
    cls?: string;
    color: string;
    colorChange: (value: string) => void;
    disabled?: boolean;
    allowedColors?: string[];
    visible?: boolean;
}

export interface ColorEditorState {
    editorVisible: boolean;
    inputValue: string;
}

const BLOCK = 'color-editor';
const HEX_MASK = {
    mask: /^[0-9a-fA-F]{0,6}$/,
    prepare: (str: string): string => str.toUpperCase()
};

export class ColorEditor extends React.PureComponent<ColorEditorProps, ColorEditorState> {
    constructor(props: ColorEditorProps) {
        super(props);

        this.state = {
            editorVisible: false,
            inputValue: ''
        };
    }

    componentDidMount(): void {
        this.setState({
            inputValue: this.props.color.replace('#', '')
        });
    }

    render(): React.ReactNode {
        const { cls, color, disabled, allowedColors } = this.props;
        const { inputValue } = this.state;
        const style = {
            backgroundColor: color
        };

        const isLimitedColorsEnabled = Boolean(allowedColors && allowedColors.length > 0);

        return (
            <div className={classNames(BLOCK, cls)}>
                <div className={Bem.cls(BLOCK, 'color')} onClick={this.onColorClick} style={style} />
                {this.renderColorPicker(isLimitedColorsEnabled)}
                <div className={Bem.cls(BLOCK, 'input')}>
                    <MaskedTextInput
                        text={inputValue}
                        theme="normal"
                        size="xs"
                        maskOptions={HEX_MASK}
                        onChange={this.onInputChange}
                        disabled={disabled || isLimitedColorsEnabled}
                    />
                </div>
            </div>
        );
    }

    private renderColorPicker(isLimitedColorsEnabled: boolean): React.ReactNode {
        const { editorVisible } = this.state;
        const { allowedColors, color, visible = true } = this.props;

        return (
            <PopupDecorator
                onOutsideClick={this.onColorCancel}
                anchor={this}
                visible={editorVisible && visible}
                cls={Bem.cls(BLOCK, 'popup', {
                    limited: isLimitedColorsEnabled
                })}
                target={'anchor'}
                hasTail={true}
                theme={ThemePopup.CLEAR}
            >
                {allowedColors && isLimitedColorsEnabled ? (
                    <LimitedColorPicker colors={allowedColors} onColorSelect={this.onColorSelect} />
                ) : (
                    <ColorPicker onChange={this.onColorPickerSelect} color={color} />
                )}
            </PopupDecorator>
        );
    }

    private onInputChange = (value: string): void => {
        if (value.length === 6) {
            this.props.colorChange(`#${value}`);
        }

        this.setState({
            inputValue: value
        });
    };

    private onColorClick = (): void => {
        if (this.props.disabled) {
            return;
        }

        this.setState({
            editorVisible: true
        });
    };

    private onColorCancel = (): void => {
        this.setState({
            editorVisible: false
        });
    };

    private onColorPickerSelect = (color: ColorResult): void => {
        this.setState({
            inputValue: color.hex.replace('#', '').toUpperCase()
        });

        this.props.colorChange(color.hex.toUpperCase());
    };

    private onColorSelect = (color: string): void => {
        this.setState({
            inputValue: color.replace('#', '')
        });

        this.props.colorChange(color.toUpperCase());
    };
}
